import React, { useEffect, useState } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useForm } from 'react-hook-form';
import { UserPlus } from 'lucide-react';

import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';
import { getContrastTextColor } from '@HELPERS/colorUtils';

import { useCreateVendor } from '@SERVICES';

import useNewVendorModal from '@HOOKS/store/modals/useNewVendorModal';

import BaseModal from '@MODALS/BaseModal';
import BasicInputValidatable from '@COMPONENTS/COMMON/inputs/input/BasicInput/BasicInputValidatable.component';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import HiddenFormButton from '@COMPONENTS/COMMON/buttons/HiddenFormButton';

import styles from './NewVendorModal.module.scss';

const cx: CX = classnames.bind(styles);

interface NewVendorFormValues {
    name: string;
    company: string;
    contactPerson: string;
    address: string;
    email: string;
    phone: string;
    website: string;
    status: 'active' | 'inactive';
    contractStatus: 'active' | 'inactive';
    notes: string;
    isVisible: boolean;
}

function NewVendorModal() {
    const [iconStrokeColor, setIconStrokeColor] = useState('#ffffff');

    const {
        actions: newVendorModalActions,
    } = useNewVendorModal(() => true);

    const {
        control,
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
    } = useForm<NewVendorFormValues>({
        defaultValues: {
            name: '',
            company: '',
            contactPerson: '',
            address: '',
            email: '',
            phone: '',
            website: '',
            status: 'active' as const,
            contractStatus: 'active' as const,
            notes: '',
            isVisible: true,
        },
    });

    const [
        createVendor,
        {
            isLoading, isSuccess, isError, error,
        },
    ] = useCreateVendor();

    const formButtonRef = React.useRef<HTMLButtonElement>(null);
    const isVisible = watch('isVisible');

    useEffect(() => {
        if (isSuccess) {
            notify.success('New vendor has been created successfully', {
                toastId: 'add-new-vendor-success',
            });
            reset();
            newVendorModalActions.close();
        }
    }, [isSuccess, newVendorModalActions, reset]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'add-new-vendor-permission-error',
                });
            } else {
                notify.error('Failed to create vendor. Please try again.', {
                    toastId: 'add-new-vendor-error',
                });
            }
        }
    }, [isError, error]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const updateIconColor = () => {
                const primaryColor = getComputedStyle(document.documentElement)
                    .getPropertyValue('--marriott-primary')
                    .trim() || '#00a4ba';
                
                // Determine appropriate stroke color for the icon
                const strokeColor = getContrastTextColor(primaryColor);
                setIconStrokeColor(strokeColor);
            };

            // Update immediately
            updateIconColor();

            // Listen for CSS variable changes (if app settings change)
            const observer = new MutationObserver(updateIconColor);
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['style']
            });

            return () => observer.disconnect();
        }
    }, []);

    const handleClose = () => {
        if (!isLoading) {
            reset();
            newVendorModalActions.close();
        }
    };

    const onSave = (formValues: NewVendorFormValues) => {
        createVendor({
            name: formValues.name,
            company: formValues.company,
            address: formValues.address,
            email: formValues.email,
            phone: formValues.phone,
            isVisible: formValues.isVisible,
            contactPerson: formValues.contactPerson,
            website: formValues.website,
            status: formValues.status,
            contractStatus: formValues.contractStatus,
            notes: formValues.notes,
        });
    };

    return (
        <BaseModal onClose={handleClose}>
            <div className={cx('new-vendor-modal')}>
                <div className={cx('modal-header')}>
                    <div className={cx('icon-wrapper')}>
                        <UserPlus 
                            className={cx('modal-icon')} 
                            size={24}
                            strokeWidth={2}
                            stroke={iconStrokeColor}
                        />
                    </div>
                    <div className={cx('header-content')}>
                        <h2 className={cx('title')}>Create New Vendor</h2>
                        <p className={cx('subtitle')}>Add a new vendor to your system</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSave)} className={cx('form')}>
                    <div className={cx('form-section')}>
                        <div className={cx('form-row')}>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<NewVendorFormValues>
                                    label="Vendor Name"
                                    placeholder="Enter vendor name"
                                    validation={{
                                        name: 'name' as const,
                                        control,
                                        rules: {
                                            required: 'Vendor name is required',
                                            minLength: {
                                                value: 2,
                                                message: 'Name must be at least 2 characters',
                                            },
                                        },
                                    }}
                                />
                            </div>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<NewVendorFormValues>
                                    label="Company"
                                    placeholder="Enter company name"
                                    validation={{
                                        name: 'company' as const,
                                        control,
                                        rules: {
                                            required: 'Company name is required',
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<NewVendorFormValues>
                                    label="Contact Person"
                                    placeholder="Enter contact person name"
                                    validation={{
                                        name: 'contactPerson' as const,
                                        control,
                                    }}
                                />
                            </div>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<NewVendorFormValues>
                                    label="Website"
                                    placeholder="Enter website URL"
                                    validation={{
                                        name: 'website' as const,
                                        control,
                                        rules: {
                                            pattern: {
                                                value: /^(https?:\/\/)?([\w-])+\.{1}([a-zA-Z]{2,63})([/\w\-._~:?#[\]@!$&'()*+,;=%]*)?$/,
                                                message: 'Please enter a valid website URL',
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<NewVendorFormValues>
                                    label="Email"
                                    placeholder="Enter email address"
                                    validation={{
                                        name: 'email' as const,
                                        control,
                                        rules: {
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address',
                                            },
                                        },
                                    }}
                                />
                            </div>
                            <div className={cx('form-group')}>
                                <BasicInputValidatable<NewVendorFormValues>
                                    label="Phone"
                                    placeholder="Enter phone number"
                                    validation={{
                                        name: 'phone' as const,
                                        control,
                                    }}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group')}>
                                <label htmlFor="status" className={cx('form-label')}>Status</label>
                                <select
                                    id="status"
                                    {...register('status')}
                                    className={cx('form-select')}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div className={cx('form-group')}>
                                <label htmlFor="contractStatus" className={cx('form-label')}>Contract Status</label>
                                <select
                                    id="contractStatus"
                                    {...register('contractStatus')}
                                    className={cx('form-select')}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group', 'full-width')}>
                                <BasicInputValidatable<NewVendorFormValues>
                                    label="Address"
                                    placeholder="Enter vendor address"
                                    validation={{
                                        name: 'address' as const,
                                        control,
                                    }}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group', 'full-width')}>
                                <label htmlFor="notes" className={cx('form-label')}>Notes</label>
                                <textarea
                                    id="notes"
                                    {...register('notes')}
                                    placeholder="Enter any additional notes about this vendor..."
                                    className={cx('form-textarea')}
                                    rows={3}
                                />
                            </div>
                        </div>

                        <div className={cx('form-row')}>
                            <div className={cx('form-group', 'visibility-group')}>
                                <label className={cx('visibility-label')}>
                                    <input
                                        type="checkbox"
                                        checked={isVisible}
                                        onChange={(e) => setValue('isVisible', e.target.checked)}
                                        className={cx('visibility-checkbox')}
                                    />
                                    <span className={cx('checkbox-indicator')} />
                                    <span className={cx('checkbox-text')}>
                                        Make vendor visible in selection lists
                                    </span>
                                </label>
                                <p className={cx('visibility-help')}>
                                    When checked, this vendor will be available for selection in documents and sections.
                                </p>
                            </div>
                        </div>
                    </div>

                    <HiddenFormButton ref={formButtonRef} />
                </form>

                <div className={cx('modal-actions')}>
                    <BasicButton
                        variant="neutral"
                        size="medium"
                        title="Cancel"
                        onClick={handleClose}
                        locked={isLoading}
                        style={{ flex: 1 }}
                    />
                    <BasicButton
                        variant="success"
                        size="medium"
                        title={isLoading ? 'Creating...' : 'Create Vendor'}
                        isProcessing={isLoading}
                        onClick={() => {
                            formButtonRef.current?.click();
                        }}
                        style={{ flex: 2 }}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

export default NewVendorModal;
